<template>
	<fieldset
		:disabled="isDisabled"
		class="PanelAddEditFeeInfo no-margin-grid"
		:style="$grid('1')"
	>
		<Panel title="Basic Info">
			<template
				#HeaderComponent
				v-if="isNewFee"
			>
				<div class="panel-buttons">
					<InputRichDropdown
						v-if="isNewFee"
						:height="35"
						v-model:saturn="selectedStoreFee"
						:list="storeFees"
						:valueMap="(pf: StoreFee) => pf"
						:display="(pf: StoreFee) => (pf.name ? pf.name : 'CUSTOM FEE')"
						@change="onFeeTypeSelected"
						nothingSelected="-- Fee Type --"
					>
					</InputRichDropdown>
				</div>
			</template>
			<div
				:style="$grid('1')"
				class="no-margin-grid"
			>
				<InputTextbox
					label="Description"
					v-model:saturn="fee.name"
					placeholder="Description"
					:invalid="v$.fee.name.$invalid"
					:disabled="isDescriptionDisabled"
				>
				</InputTextbox>

				<div
					:style="$grid('1-1-1')"
					class="no-margin-grid"
				>
					<InputCurrency
						label="Fee Amount"
						v-model:saturn="fee.amount"
						:precision="2"
						iconSide="left"
						:invalid="v$.fee.amount.$invalid"
						:disabled="isAmountDisabled"
					>
					</InputCurrency>

					<InputCurrency
						label="Fee Cost"
						v-model:saturn="fee.cost"
						:precision="2"
						iconSide="left"
						:invalid="v$.fee.cost.$invalid"
						:disabled="isCostDisabled"
					>
					</InputCurrency>

					<InputRichDropdown
						label="Itemized on Buyer's Order"
						class="itemized"
						v-model:saturn="fee.itemized"
						:list="ITEMIZED_OPTIONS"
						:valueMap="(o: any) => o.value"
						:display="(o: any) => o.display"
						:disabled="isItemizedDisabled"
					>
					</InputRichDropdown>
				</div>
			</div>
		</Panel>
	</fieldset>
</template>

<script lang="ts">
	export interface IPanelAddEditFeeInfoProps<T extends Fee> {
		isDisabled: boolean;
		bo: FIMenuBuyersOrder;
		isNewFee?: boolean;
		storeFees?: StoreFee[];
		isDealLocked?: boolean;
		bucket?: FIMenuBuyersOrderBucket;
		fee: T;
	}
</script>

<script setup lang="ts">
	// #region Imports
	import { computed, ref } from 'vue';
	import Fee from '@core/classes/Fee';
	import FIMenuBuyersOrder from '@core/classes/FIMenuBuyersOrder';
	import FIMenuBuyersOrderBucket from '@core/classes/FIMenuBuyersOrderBucket';
	import FIMenuBuyersOrderFee from '@core/classes/FIMenuBuyersOrderFee';
	import InputCurrency from '@core/components/InputCurrency.vue';
	import InputRichDropdown from '@core/components/InputRichDropdown.vue';
	import InputTextbox from '@core/components/InputTextbox.vue';
	import Panel from '@core/components/Panel.vue';
	import { required } from '@vuelidate/validators';
	import StoreFee from '@core/classes/Store/StoreFee';
	import { useVuelidate } from '@vuelidate/core';
	// #endregion

	// #region Props
	const props = defineProps<IPanelAddEditFeeInfoProps<Fee>>();
	// #endregion

	// #region states
	const isDisabled = ref<boolean>(props.isDisabled);
	const isNewFee = ref<boolean>(props.isNewFee ?? false);
	const isDealLocked = ref<boolean>(props.isDealLocked ?? false);
	const storeFees = ref<StoreFee[]>(props.storeFees?.map(f => new StoreFee(f)) ?? []);
	const bo = ref<FIMenuBuyersOrder>(props.bo);
	const selectedStoreFee = ref<StoreFee>(null);

	const fee = ref(props.fee);
	const bucket = ref<FIMenuBuyersOrderBucket>(props.bucket);

	const ITEMIZED_OPTIONS = [
		{
			value: true,
			display: 'Yes',
		},
		{
			value: false,
			display: 'No',
		},
	];

	const validations = computed(() => ({
		fee: {
			...(fee.value.validation() as any),
			name: {
				required,
				hasUniqueName,
			} as any,
		},
	}));
	const v$ = useVuelidate(validations, { fee });
	// #endregion

	// #region Computed
	const isDescriptionDisabled = computed(() => {
		return !fee.value.isEditableDesc || (isNewFee.value && !selectedStoreFee.value) || (isDealLocked.value && fee.value.itemized);
	});

	const isAmountDisabled = computed(() => {
		return !fee.value.isEditableAmount || (isNewFee.value && !selectedStoreFee.value) || isDealLocked.value;
	});

	const isCostDisabled = computed(() => {
		return !fee.value.isEditableCost || (isNewFee.value && !selectedStoreFee.value);
	});

	const isItemizedDisabled = computed(() => {
		return !fee.value.isEditableItemized || (fee.value.isEditableItemized && isDealLocked.value);
	});
	// #endregion

	// #region Methods

	// FUNCTION TO HANDLE FEE TYPE SELECTION
	function onFeeTypeSelected() {
		//Nothing selected
		if (!selectedStoreFee.value) {
			clearFeeDetails();
			return;
		}

		//FEE INFO
		if (!selectedStoreFee.value.clearDescriptionOnSelection) {
			fee.value.name = selectedStoreFee.value.name;
		} else {
			fee.value.name = null;
		}
		fee.value.amount = selectedStoreFee.value.amount;
		fee.value.cost = selectedStoreFee.value.cost;
		fee.value.isEditableDesc = selectedStoreFee.value.isEditableDesc;
		fee.value.isEditableCost = selectedStoreFee.value.isEditableCost;
		fee.value.isEditableAmount = selectedStoreFee.value.isEditableAmount;
		fee.value.isEditableItemized = selectedStoreFee.value.isEditableItemized;
		fee.value.itemized = fee.value.isEditableItemized ? false : selectedStoreFee.value.itemized;
		fee.value.isFinanceCharge = selectedStoreFee.value.isFinanceCharge;
		fee.value.profitFlag = selectedStoreFee.value.profitFlag;
		fee.value.source = selectedStoreFee.value.source;
		fee.value.clearDescriptionOnSelection = selectedStoreFee.value.clearDescriptionOnSelection;

		//SET UP BUCKET & TITLE ID
		let newBucket = null;
		if (selectedStoreFee.value.titleId) {
			newBucket = bo.value.buckets.find((b: FIMenuBuyersOrderBucket) => b.bucketType == 'Fees' && b.titleId == selectedStoreFee.value.titleId);
		}

		if (!newBucket) {
			newBucket = bo.value.addNewDefaultBucket(bucket.value.bucketType, 0, 0, null, selectedStoreFee.value.titleId);
		}

		// We used to assign with 'bucket.value = newBucket', but this would cause de-sync between local 'bucket' ref and passed 'bucket' prop
		// This Object.assign is not ideal, but is necessary as we are mutating props directly instead of using v-model.
		Object.assign(bucket.value, newBucket);

		fee.value.titleId = bucket.value.titleId;
	}

	// We moved this logic outside of FIMenuBuyersOrderFee because of base Fee's validation method
	function hasUniqueName() {
		return !bo.value.fees.find(
			(boFee: FIMenuBuyersOrderFee) => fee.value.id !== boFee.id && boFee.name && fee.value.name && boFee.name.toLowerCase() === fee.value.name.toLowerCase(),
		);
	}

	// FUNCTION TO CLEAR FEE DETAILS
	function clearFeeDetails() {
		fee.value.name = null;
		fee.value.amount = null;
		fee.value.cost = null;
	}

	// #endregion
</script>

<style scoped></style>
